import React, { useState, useEffect, useCallback } from "react";
import { apiUrl } from "../taskpane";

const LEVEL_COLORS = {
  "window.onerror":     { bg: "rgba(239,68,68,0.12)",  border: "#ef4444", label: "JS Error" },
  "unhandledrejection": { bg: "rgba(249,115,22,0.12)", border: "#f97316", label: "Async Error" },
  "manual-test":        { bg: "rgba(34,197,94,0.12)",  border: "#22c55e", label: "Test" },
};

function levelStyle(context) {
  return LEVEL_COLORS[context] || {
    bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.3)", label: context || "Error",
  };
}

function formatTs(iso) {
  try { return new Date(iso).toLocaleString("ka-GE", { dateStyle: "short", timeStyle: "medium" }); }
  catch { return iso; }
}

async function fetchErrors(token) {
  const res = await fetch(apiUrl("frontend-errors"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  return res.json();
}

async function deleteAllErrors(token) {
  const res = await fetch(apiUrl("frontend-errors"), {
    method: "DELETE", headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
}

async function deleteOneError(token, id) {
  const res = await fetch(apiUrl(`frontend-errors/${id}`), {
    method: "DELETE", headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
}

export default function AppErrorLogPage({ token, onBack }) {
  const [errors,     setErrors]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [expanded,   setExpanded]   = useState({});

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setFetchError("");
    fetchErrors(token)
      .then(setErrors)
      .catch((e) => setFetchError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleClearAll = async () => {
    try { await deleteAllErrors(token); setErrors([]); }
    catch (e) { setFetchError(e.message); }
  };

  const handleDeleteOne = async (id) => {
    try {
      await deleteOneError(token, id);
      setErrors((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { setFetchError(e.message); }
  };

  const toggleStack = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{
      minHeight: "100vh", background: "var(--page-gradient)",
      padding: "20px 16px", color: "var(--text-primary)",
      fontFamily: "Arial, sans-serif",
    }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
        <button className="link" onClick={onBack}>← უკან</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>შეცდომების ჟურნალი</h2>
        </div>
        <span style={{ fontSize: 11, color: "var(--text-muted)", marginRight: 4 }}>
          {errors.length} ჩანაწერი
        </span>
        <button
          onClick={load}
          style={{
            background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.25)",
            borderRadius: 6, padding: "4px 8px", cursor: "pointer",
            fontSize: 13, color: "var(--text-muted)",
          }}
          title="განახლება"
        >
          ↺
        </button>
        {errors.length > 0 && (
          <button
            onClick={handleClearAll}
            style={{
              background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 6, padding: "4px 10px", cursor: "pointer",
              fontSize: 11, color: "#f87171",
            }}
          >
            ყველა
          </button>
        )}
      </div>

      {/* ── Fetch error ── */}
      {fetchError && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#fca5a5",
          marginBottom: 14,
        }}>
          {fetchError}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)", fontSize: 13 }}>
          იტვირთება...
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && !fetchError && errors.length === 0 && (
        <div style={{ textAlign: "center", padding: "56px 0", color: "var(--text-muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>
            შეცდომები არ არის
          </div>
          <div style={{ fontSize: 12 }}>პროგრამა სწორად მუშაობს</div>
        </div>
      )}

      {/* ── Error list ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {errors.map((err) => {
          const style  = levelStyle(err.context);
          const isOpen = !!expanded[err.id];
          return (
            <div
              key={err.id}
              style={{
                background: style.bg, border: `1px solid ${style.border}`,
                borderRadius: 8, overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "10px 12px", display: "flex", gap: 8,
                  alignItems: "flex-start",
                  cursor: err.stack ? "pointer" : "default",
                }}
                onClick={() => err.stack && toggleStack(err.id)}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700, background: style.border,
                  color: "#fff", borderRadius: 3, padding: "2px 6px",
                  whiteSpace: "nowrap", marginTop: 1, flexShrink: 0,
                }}>
                  {style.label}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, wordBreak: "break-word", lineHeight: 1.4 }}>
                    {err.message}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                    {formatTs(err.created_at)}
                    {err.user_name && <span style={{ marginLeft: 8 }}>· {err.user_name}</span>}
                  </div>
                </div>
                {err.stack && (
                  <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteOne(err.id); }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "var(--text-muted)", fontSize: 14, padding: "0 2px", flexShrink: 0,
                  }}
                  title="წაშლა"
                >
                  ✕
                </button>
              </div>
              {isOpen && err.stack && (
                <pre style={{
                  margin: 0, padding: "8px 12px",
                  borderTop: `1px solid ${style.border}`,
                  fontSize: 10, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-all",
                  color: "var(--text-muted)", background: "rgba(0,0,0,0.15)",
                  maxHeight: 200, overflowY: "auto",
                }}>
                  {err.stack}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
